import { createSlice } from '@reduxjs/toolkit';
import { getAlertIdentifier } from 'features/Weather/features/WeatherAlerts/utils/alertData.utils';
import { getAlertWithSeverityLabel } from 'features/Weather/features/WeatherAlerts/utils/alertSeverity.utils';
import { closeAlertThunk } from '../thunks/alertListThunks';

const alertListSlice = createSlice({
  name: 'alertList',
  initialState: {
    alertList: [],
    showCloseAlertModal: false, // whether to show the modal to close an alert
    listConfig: {
      pageIndex: 1,
      severitySortDirection: null, // true is ascending, false is descending
      showLocalOnly: false, // whether to only show alerts local to the user's current location
      maxDist: 100, // max distance from the user's current location
      searchTerm: '', // the current search term used to filter the alert list
      alertTypes: [], // selected exact alert type names (multi-select)
    },
    selectedAlertConfig: {
      selectedAlert: null, // the ID of the currently selected alert
      mapCenter: null, // the center coordinates for the map when this alert is selected
      mapPolygon: null, // the polygon coordinates for the map when this alert is selected
    },
  },
  reducers: {
    setAlertList: (state, action) => {
      state.alertList = Array.isArray(action.payload)
        ? action.payload.map(getAlertWithSeverityLabel)
        : [];
    },
    setListConfig: (state, action) => {
      state.listConfig = {
        ...state.listConfig,
        ...action.payload,
      };
    },
    setPageIndex: (state, action) => {
      state.listConfig.pageIndex = action.payload;
    },
    setSeveritySortDirection: (state, action) => {
      state.listConfig.severitySortDirection = action.payload;
    },
    setShowLocalOnly: (state, action) => {
      state.listConfig.showLocalOnly = action.payload;
    },
    setMaxDist: (state, action) => {
      state.listConfig.maxDist = action.payload;
    },
    setSearchTerm: (state, action) => {
      state.listConfig.searchTerm = action.payload;
    },
    setAlertTypes: (state, action) => {
      state.listConfig.alertTypes = Array.isArray(action.payload)
        ? action.payload
        : action.payload
          ? [action.payload]
          : [];
    },

    setSelectedAlertConfig: (state, action) => {
      state.selectedAlertConfig = {
        ...state.selectedAlertConfig,
        ...action.payload,
      };
    },
    setSelectedAlert: (state, action) => {
      state.selectedAlertConfig.selectedAlert = action.payload;
    },
    setMapCenter: (state, action) => {
      state.selectedAlertConfig.mapCenter = action.payload;
    },
    setMapPolygon: (state, action) => {
      state.selectedAlertConfig.mapPolygon = action.payload;
    },
    setShowCloseAlertModal: (state, action) => {
      state.showCloseAlertModal = !!action.payload; // ensure the value is a boolean
    },
    removeAlertById: (state, action) => {
      const alertId = action.payload;
      state.alertList = state.alertList.filter(
        (alert, index) => getAlertIdentifier(alert, index) !== alertId,
      );
    },
  },
  extraReducers: (builder) => {
    builder.addCase(closeAlertThunk.fulfilled, (state, action) => {
      const { alertId } = action.payload;

      state.alertList = state.alertList.filter(
        (alert, index) => getAlertIdentifier(alert, index) !== alertId,
      );

      state.showCloseAlertModal = false;

      if (state.selectedAlertConfig.alertId === alertId) {
        state.selectedAlertConfig = {
          alertId: null,
          mapCenter: null,
          mapPolygon: null,
        };
      }
    });
  },
});

export const {
  setAlertList,
  setShowCloseAlertModal,
  setListConfig,
  setPageIndex,
  setSeveritySortDirection,
  setShowLocalOnly,
  setAlertTypes,
  setMaxDist,
  setSelectedAlertConfig,
  setSelectedAlert,
  setMapCenter,
  setMapPolygon,
  setSearchTerm,
} = alertListSlice.actions;

export default alertListSlice.reducer;
